import { PAGES } from "@dune/types";
import type { SubmissionParticipantWithValidDecennialCertificate } from "@dune/types";
import { useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAddDesignerParticipantToReception } from "../../../../api/mutations/constructionSiteSubmission.mutations";
import { useGetConstructionSiteReceptionParticipants } from "../../../../api/queries/constructionSiteSubmission.queries";
import { DesignerToSubmissionForm } from "../../../../component/constructionSites/submission/participants/DesignerToSubmissionForm";
import PageLayoutWithTabs from "../../../../component/layout/PageLayoutWithTabs";
import { useDesignerToSubmissionForm } from "../../../../hooks/useParticipantToSubmissionForm";
import { ParticipantDocumentsReceptionTable } from "../../components/intervenants/ParticipantDocumentsReceptionTable";

const AddDesignerReceptionPage = () => {
	const navigate = useNavigate();
	const { constructionSiteId } = useParams<{ constructionSiteId: string }>();
	const [searchParams] = useSearchParams();
	const returnUrl = searchParams.get("returnUrl");

	const { data: receptionParticipants } =
		useGetConstructionSiteReceptionParticipants(Number(constructionSiteId));
	// Fees now come from reception participants

	const firstDesigner = useMemo(() => {
		const rp = receptionParticipants?.find((p) => p.type === "DESIGNER");
		if (!rp) return null;
		const isNewParticipant = !rp.isFromSubmission;
		const participantWithFlag = {
			...rp.participant,
			isNewForReception: isNewParticipant,
		};

		const obj = {
			id: rp.id,
			type: rp.type,
			designerType: rp.designerType ?? null,
			totalFeesHT: rp.totalFeesHT ?? 0,
			totalFeesTTC: rp.totalFeesTTC ?? 0,
			totalMissionsOrActivitiesOrMaterialTypes: [],
			participant: participantWithFlag,
			decennialCertificatesOfSubmissionOrValidFromOthers: null,
			insurer: null,
		} satisfies SubmissionParticipantWithValidDecennialCertificate;
		return obj;
	}, [receptionParticipants]);

	function goBack() {
		if (returnUrl) navigate(returnUrl);
		else if (constructionSiteId)
			navigate(PAGES.PROJECTS.reception.withParams(constructionSiteId, 3));
		else navigate(PAGES.PROJECTS.index);
	}

	const numericConstructionSiteId = constructionSiteId
		? Number(constructionSiteId)
		: 0;

	const addDesigner = useAddDesignerParticipantToReception(
		numericConstructionSiteId,
	);

	const formApi = useDesignerToSubmissionForm({
		mode: "add",
		onSubmit: (data) => addDesigner.mutate(data, { onSuccess: () => goBack() }),
	});

	if (!constructionSiteId) return null;

	return (
		<PageLayoutWithTabs title="Ajouter un concepteur">
			<FormProvider {...formApi.form}>
				<form onSubmit={formApi.handleSubmit}>
					<DesignerToSubmissionForm
						constructionSiteId={numericConstructionSiteId}
						onSubmit={formApi.handleSubmit}
						onCancel={goBack}
						isSubmitting={addDesigner.isPending}
						documentsSlot={
							firstDesigner && (
								<ParticipantDocumentsReceptionTable
									submissionParticipant={firstDesigner}
									constructionSiteId={numericConstructionSiteId}
								/>
							)
						}
					/>
				</form>
			</FormProvider>
		</PageLayoutWithTabs>
	);
};

export default AddDesignerReceptionPage;
